import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import ReactECharts from "echarts-for-react";
import {
  Shield, CheckCircle, AlertTriangle, ArrowLeft, Users, FileText, Scale
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const HRCompliancePage = () => {
  const [complianceData, setComplianceData] = useState({ compliance_items: [], summary: {} });
  const [labourLaw, setLabourLaw] = useState({ labour_checks: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [hrRes, labourRes] = await Promise.all([
          axios.get(`${API}/admin/compliance/hr`, { headers }),
          axios.get(`${API}/admin/compliance/labour-law`, { headers })
        ]);
        setComplianceData(hrRes.data);
        setLabourLaw(labourRes.data);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const { summary, compliance_items } = complianceData;

  const complianceGaugeOptions = {
    backgroundColor: 'transparent',
    series: [{
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      min: 0,
      max: 100,
      radius: '100%',
      pointer: { show: true, length: '55%', width: 5, itemStyle: { color: '#c9a227' } },
      axisLine: { lineStyle: { width: 12, color: [[0.5, '#ef4444'], [0.75, '#eab308'], [1, '#22c55e']] } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      detail: { valueAnimation: true, fontSize: 24, color: '#c9a227', offsetCenter: [0, '30%'], formatter: '{value}%' },
      data: [{ value: summary.compliance_rate || 0 }]
    }]
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "compliant": return "bg-green-500/20 text-green-400";
      case "warning": return "bg-yellow-500/20 text-yellow-400";
      default: return "bg-red-500/20 text-red-400";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Compliance Data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="hr-compliance-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin" className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            HR Compliance & Governance
          </h1>
          <p className="text-slate-400 text-sm">Contract, certification, and labour law compliance</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{summary.total_employees || 0}</p>
            <p className="text-xs text-slate-400">Total Employees</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{summary.compliant || 0}</p>
            <p className="text-xs text-slate-400">Fully Compliant</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{summary.non_compliant || 0}</p>
            <p className="text-xs text-slate-400">Non-Compliant</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Shield className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{summary.compliance_rate || 0}%</p>
            <p className="text-xs text-slate-400">Compliance Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {summary.non_compliant > 0 && (
        <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm mb-6">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{summary.non_compliant} employees have compliance issues that need attention</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Compliance Gauge */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Overall Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={complianceGaugeOptions} style={{ height: '200px' }} />
            <p className="text-center text-slate-400 text-sm mt-2">
              {summary.compliance_rate >= 90 ? 'Excellent' : summary.compliance_rate >= 70 ? 'Good' : 'Needs Attention'}
            </p>
          </CardContent>
        </Card>

        {/* Labour Law Compliance */}
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200 flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#c9a227]" />
              Labour Law Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {labourLaw.labour_checks?.map((check, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/30 rounded-sm">
                  <span className="text-white text-sm">{check.check}</span>
                  <Badge className={getStatusBadge(check.status)}>{check.status}</Badge>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-slate-400 text-sm">Jurisdictions: {labourLaw.jurisdictions?.join(", ")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Compliance Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Employee Compliance Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Contract</th>
                  <th>Certifications</th>
                  <th>Issues</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {compliance_items.map((item) => (
                  <tr key={item.employee_id}>
                    <td className="text-white font-medium">{item.employee_name}</td>
                    <td className="text-slate-300">{item.department}</td>
                    <td>
                      <Badge className={item.contract_status === "active" ? "bg-green-500/20 text-green-400" : item.contract_status === "expiring_soon" ? "bg-yellow-500/20 text-yellow-400" : "bg-red-500/20 text-red-400"}>
                        {item.contract_status?.replace("_", " ") || "missing"}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-300">{item.certifications_count}</span>
                        {item.expiring_certifications > 0 && (
                          <Badge className="bg-yellow-500/20 text-yellow-400">{item.expiring_certifications} expiring</Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      {item.issues?.length > 0 ? (
                        <div className="space-y-1">
                          {item.issues.slice(0, 2).map((issue, idx) => (
                            <p key={idx} className="text-red-400 text-xs">{issue}</p>
                          ))}
                        </div>
                      ) : (
                        <span className="text-green-400 text-sm">None</span>
                      )}
                    </td>
                    <td>
                      <Badge className={item.compliant ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {item.compliant ? "Compliant" : "Action Required"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRCompliancePage;
